/**
 * Per-window availability probes for the zero-results rescue (#1265).
 *
 * One `limit=1` `feed/properties` query per window, fired concurrently. This is
 * the SAME request the grid makes for a dated search (data.ts `fetchProperties`),
 * and the same probe the toolkit's own REST endpoint runs per range
 * (includes/FlexibleSearch/RestController.php:273-290 —
 * `get_properties(limit=1, checkin, checkout, guests)`): Central applies the
 * availability constraint before paginating (PropertyController::listProperties),
 * so a non-empty `result` is the verdict. Because the probe carries the
 * visitor's OTHER filters too (everything but the sort and the map area), a
 * confirmed window is guaranteed to render results when picked — the toolkit
 * only checks dates + guests.
 *
 * Cost on beta feed 8 (2026-08-27 probe): ~0.7 s per cold window, ~1.7 s wall
 * for the default six fired together, ~130 ms once Central has cached that
 * (checkin, checkout, guests) triple.
 */
import { useEffect, useMemo, useState } from 'react'
import { useQueries } from '@tanstack/react-query'
import type { VisitorFilter } from '../../../config'
import { fetchProperties } from '../../../data'
import { type RescueWindow, type Verdict, windowKey } from './zeroResultsRescue'

/** `RestController::CACHE_TTL` — a verdict is good for five minutes. */
export const RESCUE_VERDICT_STALE_MS = 5 * 60 * 1000

/**
 * Filter keys a probe neither sends nor keys on. The dates are supplied per
 * window. `sort_by` only orders a result set and never changes its membership,
 * so a visitor re-sorting a zero-results page must not re-fire the batch under
 * a fresh cache key (Codex round 1: up to 8 probes per sort change, for the
 * same answers). `map_bounds` is listed as defence in depth only: the rescue
 * is not offered at all while an applied search-as-move area narrows the
 * results (`rescueApplies`), so a probe never carries bounds in practice; the
 * entry keeps a future relaxation of that gate from re-opening the
 * probes-per-drag fan-out. `price_field` deliberately STAYS: Central applies
 * `price_min`/`price_max` to that column, so dropping it could make a probe
 * disagree with the grid — and it derives from widget options, never from a
 * visitor gesture, so it cannot fork keys.
 */
export const PROBE_IGNORED_KEYS = ['checkin', 'checkout', 'sort_by', 'map_bounds'] as const

/** The visitor's search minus the keys above — what every probe asks. */
export function probeFilters(filters: Partial<VisitorFilter>): Partial<VisitorFilter> {
  const out: Partial<VisitorFilter> = { ...filters }
  for (const key of PROBE_IGNORED_KEYS) delete out[key]
  return out
}

/**
 * Key: the probe filters plus the window. The empty-state box and, after a
 * pivot, the notice's "other available dates" strip ask about the same windows
 * for the same search — they share one cache entry, so the notice never
 * re-probes a window the box already got an answer for. A window Central could
 * NOT answer is asked again on the next mount (an errored query holds no data):
 * the module's "null is never cached" rule.
 */
export function rescueWindowQueryKey(
  feedId: number,
  platformUuid: string | undefined,
  filters: Partial<VisitorFilter>,
  w: { checkin: string; checkout: string },
) {
  return ['explore-rescue-window', feedId, platformUuid, probeFilters(filters), w.checkin, w.checkout] as const
}

/**
 * Verdict from one probe response. Only a well-formed `ok` envelope with an
 * array `result` answers the question; a 200 carrying an error envelope or a
 * malformed body is "could not answer" (null), never a prune.
 */
export function verdictFromResponse(data: unknown): Verdict {
  if (!data || typeof data !== 'object') return null
  const { status, result } = data as { status?: unknown; result?: unknown }
  if (status !== 'ok' || !Array.isArray(result)) return null
  return result.length > 0
}

/** Thrown for a 200 whose body cannot answer — keeps it OUT of the cache, like a 503. */
export class RescueProbeUnanswerable extends Error {
  constructor() {
    super('Zero-results rescue probe: response could not answer availability')
    this.name = 'RescueProbeUnanswerable'
  }
}

/**
 * The probe itself: the grid's request with the window's dates, reduced to a
 * boolean verdict INSIDE the query so only real answers are ever cached. An
 * unanswerable 200 throws (Codex round 2): a resolved-but-null verdict would
 * sit in the cache as fresh data for five minutes and the notice would never
 * re-ask it, unlike a 503.
 */
export async function probeWindow(
  feedId: number,
  filters: Partial<VisitorFilter>,
  w: { checkin: string; checkout: string },
  platformUuid: string | undefined,
): Promise<boolean> {
  const data = await fetchProperties(feedId, { ...probeFilters(filters), checkin: w.checkin, checkout: w.checkout }, 1, 1, platformUuid)
  const verdict = verdictFromResponse(data)
  if (verdict === null) throw new RescueProbeUnanswerable()
  return verdict
}

export type WindowVerdicts = {
  /** `undefined` until that window's probe has answered. */
  verdictFor: (w: RescueWindow) => Verdict | undefined
  /** Every enabled probe has answered (success or error). */
  settled: boolean
}

/**
 * How long the visitor's filters must hold still before a batch fires.
 *
 * The per-window cap bounds ONE batch at `MAX_VERIFIABLE_WINDOWS`, but the
 * filters are part of the query key, so every edit mints a fresh batch and the
 * 5-minute cache cannot help. Measured on a zero-results page: three clicks of
 * the guests stepper produced 24 probes + 5 grid queries = 29 requests in ~6s,
 * each window re-probed once per filter combination, with no batch cancelled.
 * `throttle.public-api` is 60/min per IP across ALL endpoints, so ~7 such
 * adjustments exhaust the visitor's minute and the 429 lands on the grid's own
 * query — the rescue does not degrade, the page breaks, for the visitor most
 * likely to be adjusting filters.
 *
 * Settling first collapses a burst into a single batch (29 -> 13 on that
 * trace). Costless to the UX: the box already renders every chip unverified
 * and folds verdicts in as they land, so this just widens the existing
 * unverified moment rather than introducing a new state.
 */
export const PROBE_SETTLE_MS = 400

export function useWindowAvailability(
  feedId: number,
  filters: Partial<VisitorFilter>,
  windows: RescueWindow[],
  platformUuid: string | undefined,
  enabled: boolean,
): WindowVerdicts {
  // Probe only the SETTLED filter set. Deliberately gating rather than
  // debouncing the value itself: probes carry the visitor's other filters so a
  // confirmed window is guaranteed to render results under them, and firing
  // against a stale set would put a ✓ on a window that no longer holds.
  // Unverified-for-a-moment is honest; verified-against-the-wrong-search is not.
  const filtersKey = JSON.stringify(probeFilters(filters))
  const [armedKey, setArmedKey] = useState<string | null>(null)
  useEffect(() => {
    if (!enabled) {
      setArmedKey(null)
      return
    }
    const timer = setTimeout(() => setArmedKey(filtersKey), PROBE_SETTLE_MS)
    return () => clearTimeout(timer)
  }, [enabled, filtersKey])
  const probesEnabled = enabled && armedKey === filtersKey

  const results = useQueries({
    queries: windows.map((w) => ({
      queryKey: rescueWindowQueryKey(feedId, platformUuid, filters, w),
      queryFn: () => probeWindow(feedId, filters, w, platformUuid),
      enabled: probesEnabled,
      staleTime: RESCUE_VERDICT_STALE_MS,
      // Any error — transport, HTTP, or an unanswerable 200 — is "could not
      // answer": surfaced as an unknown verdict at once, never hammered, never
      // cached (the module's null-is-never-cached rule).
      retry: false,
    })),
  })

  return useMemo(() => {
    const byKey = new Map<string, Verdict | undefined>()
    let settled = probesEnabled
    windows.forEach((w, i) => {
      const r = results[i]
      if (!r) {
        settled = false
        return
      }
      if (r.isSuccess) {
        byKey.set(windowKey(w), r.data)
      } else if (r.isError) {
        byKey.set(windowKey(w), null)
      } else {
        settled = false
      }
    })
    return { verdictFor: (w: RescueWindow) => byKey.get(windowKey(w)), settled }
  }, [results, windows, probesEnabled])
}
